import { useState, useEffect, useRef } from 'react'
import { Phone, Search, X, ChevronRight, Smartphone } from 'lucide-react'
import { Modal } from '../ui/Modal'
import supabase from '../../lib/supabase'
import { useConnectionRecovered } from '../../hooks/useConnectionRecovered'
import { placeCall, dialNatively } from '../../lib/rcDial'

export default function DialerModal({ isOpen, onClose, onDialComplete }) {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [selected, setSelected] = useState(null) // { id, first_name, last_name, phones }
  const [recoverTick, setRecoverTick] = useState(0)
  const [dialing, setDialing] = useState(null)   // the number currently being placed
  const [ringing, setRinging] = useState(null)   // { number, contactName } once RC has it
  const pendingRef = useRef(null)

  // Re-run the current search after the connection un-wedges
  useConnectionRecovered(() => { if (search.trim().length >= 2) setRecoverTick(t => t + 1) })

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setSearch('')
      setResults([])
      setSelected(null)
      setDialing(null)
      setRinging(null)
      pendingRef.current = null
    }
  }, [isOpen])

  // Return-from-dialer detection
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && pendingRef.current) {
        const contactId = pendingRef.current
        pendingRef.current = null
        onClose()
        onDialComplete(contactId)
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [onClose, onDialComplete])

  // Debounced contact search
  useEffect(() => {
    if (!search.trim() || search.length < 2) { setResults([]); return }
    const t = setTimeout(async () => {
      const words = search.trim().split(/\s+/).filter(Boolean)
      let ids = null
      for (const word of words) {
        // search_names = name columns + aliases, so a contact stays findable
        // under a former name after a rename.
        const { data: wd } = await supabase
          .from('contacts')
          .select('id')
          .or(`first_name.ilike.%${word}%,last_name.ilike.%${word}%,search_names.ilike.%${word}%`)
          .limit(200)
        const wordIds = new Set((wd || []).map(r => r.id))
        ids = ids === null ? wordIds : new Set([...ids].filter(id => wordIds.has(id)))
      }
      if (!ids || ids.size === 0) { setResults([]); return }
      const { data } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, title, phones, organization:companies(name)')
        .in('id', [...ids])
        .limit(8)
      setResults(data || [])
    }, 220)
    return () => clearTimeout(t)
  }, [search, recoverTick])

  const contactLabel = (c) => `${c.first_name} ${c.last_name}`.trim()

  // RingCentral two-legged: your own phone rings first, then RC connects them —
  // so the recipient sees the Chatrone DID and the transcript pipeline picks the
  // call up automatically. rc-dial writes the call_log row, so no QuickLog here.
  const dial = async (contact, number) => {
    setDialing(number)
    try {
      const result = await placeCall({
        toNumber:    number,
        contactId:   contact.id,
        contactName: contactLabel(contact),
      })
      if (result.via === 'ringcentral') {
        setRinging({ number, contactName: contactLabel(contact) })
        return
      }
      // Fell through to the native dialer — the OS is taking over, and nothing
      // was logged server-side, so arm the return handler to open QuickLog.
      pendingRef.current = contact.id
    } finally {
      setDialing(null)
    }
  }

  // Explicit "use my phone instead" — same return-and-log flow as the fallback.
  const dialFromPhone = (contact, number) => {
    pendingRef.current = contact.id
    dialNatively(number)
  }

  const validPhones = (contact) =>
    (contact.phones || []).filter(p => p.number && p.number.trim())

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Dial" size="sm">
      <div className="p-4 space-y-4">

        {/* Search */}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-400" />
          <input
            autoFocus
            className="input-field pl-8"
            placeholder="Search contacts..."
            value={search}
            onChange={e => { setSearch(e.target.value); setSelected(null) }}
          />
          {search && (
            <button
              onClick={() => { setSearch(''); setSelected(null); setResults([]) }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-warm-400 hover:text-warm-600"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Contact selected — show phone options */}
        {selected && (
          <div className="rounded-xl border border-warm-200 bg-warm-50 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-warm-900">
                  {selected.first_name} {selected.last_name}
                </p>
                {selected.organization?.name && (
                  <p className="text-xs text-warm-500">{selected.organization.name}</p>
                )}
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-warm-400 hover:text-warm-600"
              >
                <X size={14} />
              </button>
            </div>

            {ringing ? (
              <div className="rounded-lg bg-white border border-sage-200 px-3 py-3 space-y-1">
                <p className="text-sm font-semibold text-sage-700">Your phone is ringing</p>
                <p className="text-xs text-warm-500">
                  Answer it and RingCentral connects {ringing.contactName} at {ringing.number}.
                </p>
                <p className="text-[11px] text-warm-400">
                  Already logged — notes fill in from the transcript after the call.
                </p>
                <button
                  onClick={onClose}
                  className="mt-1 text-xs font-medium text-navy-600 hover:text-navy-700"
                >
                  Done
                </button>
              </div>
            ) : validPhones(selected).length === 0 ? (
              <p className="text-xs text-warm-400 italic">No phone numbers on file</p>
            ) : (
              <div className="space-y-1.5">
                {validPhones(selected).map((p, i) => (
                  <div
                    key={i}
                    className="flex items-stretch gap-1.5"
                  >
                    <button
                      onClick={() => dial(selected, p.number)}
                      disabled={!!dialing}
                      className="flex-1 min-w-0 flex items-center justify-between px-3 py-2.5 rounded-lg bg-white border border-warm-200 hover:border-navy-300 hover:bg-navy-50 transition-colors group disabled:opacity-60"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Phone size={14} className="text-warm-400 group-hover:text-navy-500 shrink-0" />
                        <div className="text-left min-w-0">
                          <p className="text-sm font-medium text-warm-800 group-hover:text-navy-700 truncate">
                            {p.number}
                          </p>
                          {p.type && (
                            <p className="text-[10px] text-warm-400 uppercase tracking-wide">{p.type}</p>
                          )}
                        </div>
                      </div>
                      {dialing === p.number
                        ? <span className="text-[10px] text-navy-500 shrink-0">Dialing…</span>
                        : <ChevronRight size={14} className="text-warm-300 group-hover:text-navy-400 shrink-0" />}
                    </button>
                    <button
                      onClick={() => dialFromPhone(selected, p.number)}
                      disabled={!!dialing}
                      title="Call from this phone instead (personal caller ID, log it yourself)"
                      className="px-2.5 rounded-lg bg-white border border-warm-200 text-warm-400 hover:text-navy-600 hover:border-navy-300 transition-colors disabled:opacity-60"
                    >
                      <Smartphone size={14} />
                    </button>
                  </div>
                ))}
                <p className="text-[10px] text-warm-400 pt-0.5">
                  Calls go out on your Chatrone line. Tap the phone icon to use this device instead.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Search results */}
        {!selected && results.length > 0 && (
          <div className="space-y-1">
            {results.map(c => {
              const phones = validPhones(c)
              return (
                <button
                  key={c.id}
                  onClick={() => setSelected(c)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-warm-100 transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-navy-50 border border-navy-100 flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-bold text-navy-500">
                      {c.first_name[0]}{c.last_name[0]}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-warm-800 truncate">
                      {c.first_name} {c.last_name}
                    </p>
                    <p className="text-xs text-warm-400 truncate">
                      {c.organization?.name || c.title || ''}
                    </p>
                  </div>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${
                    phones.length > 0
                      ? 'bg-sage-100 text-sage-700'
                      : 'bg-warm-100 text-warm-400'
                  }`}>
                    {phones.length > 0 ? `${phones.length} #` : 'No #'}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {!selected && search.length >= 2 && results.length === 0 && (
          <p className="text-sm text-warm-400 text-center py-4">No contacts found</p>
        )}

        {!selected && search.length < 2 && (
          <p className="text-xs text-warm-400 text-center py-2">Type a name to search</p>
        )}
      </div>
    </Modal>
  )
}
